// Request and response types for `/api/v1`.
//
// Conventions, per the API contract, applied without exception:
//  * keys are the Postgres `snake_case` column names verbatim — one vocabulary
//    from the database to the React component;
//  * money is a DECIMAL STRING, never a JSON number;
//  * timestamps are RFC 3339 UTC strings, dates are `yyyy-MM-dd`, time-of-day is
//    `HH:mm`;
//  * every user-mutable row carries `row_version` for optimistic concurrency;
//  * a key ABSENT from a PATCH body means "leave unchanged"; a key present with
//    `null` means "clear this field". `Clearable<T>` expresses that.

import type { ReportCriteria, ReportInvoiceRow, ReportSort } from './report.js'
import type {
  AccountSource,
  AnalyticsDimension,
  AnalyticsSection,
  AssignmentAction,
  AssignmentCompanyScope,
  AssignmentRequestStatus,
  AssignmentRole,
  CompanyNameSource,
  CompanySource,
  DayOfWeek,
  DecisionOutcome,
  DecisionSource,
  DecisionStep,
  RecordedDecisionStep,
  DocumentType,
  EntityType,
  ExportStatus,
  UploadNotificationKind,
  ListedNotificationType,
  NotificationType,
  FlagResolution,
  InvoiceStatus,
  LineItemDetailKind,
  MatchSource,
  NodeKey,
  PipelineStepKey,
  ReasonCategory,
  ReferenceSection,
  RouteSource,
  RoutingRuleSource,
  RulesSection,
  SourceSystem,
  SpendGrouping,
  SubscriptionStatus,
  UploadPhase,
  UploadStatus,
  UserRole,
  VehicleStatus,
  VendorAliasSource,
  ViewSessionKind,
  CodingRuleSource,
  OversightRuleField,
  OversightRuleOperator,
  OversightRuleScope,
  DepartmentSource,
  WorkspaceKind,
  DocumentForm,
} from './enums.js'
import type { MoneyString } from './money.js'

/** RFC 3339 UTC, e.g. `"2026-08-04T14:30:00Z"`. */
type Timestamp = string
/** `yyyy-MM-dd`. */
type DateOnly = string
/** `HH:mm`, on a quarter hour — the reminder scheduler ticks every fifteen minutes. */
type TimeOfDay = string
/** `numeric(14,4)` as a decimal string, e.g. `"2.5000"`. */
type QuantityString = string
/** A UNIT PRICE as a decimal string, EXACT at any scale up to 10 decimal places
    (`PRICE_MAX_DECIMALS`) — `"0.0049"`, `"12.50"` (D-249). The one amount that is
    not whole cents; every `MoneyString` is. Never exported. */
type PriceString = string
/** Integer 0–100. */
export type Confidence = number
type Uuid = string

/**
 * A PATCH field that can be explicitly cleared. Absent = unchanged, `null` =
 * clear. A handler that treats the two alike silently discards field clears at
 * Import Control.
 */
export type Clearable<T> = { [K in keyof T]?: T[K] | null }

// ---------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------

export interface Paginated<T> {
  data: T[]
  /** `null` on the last page. Opaque — clients must not construct or inspect it. */
  next_cursor: string | null
  /** Present only where the screen shows a count and the query can produce one cheaply. */
  total?: number
}

export type ErrorCode =
  // 400
  | 'invalid_request'
  | 'validation_failed'
  | 'unknown_parameter'
  | 'invalid_cursor'
  | 'too_many_ids'
  | 'row_version_required'
  | 'unsupported_outcome'
  | 'reason_required'
  // A decision whose outcome demands a category arrived without one
  // (owner, 2026-09-15). Distinct from `reason_required` because the two
  // are now different fields with different rules: the category is
  // required on Deny, Not An Invoice and Move To Manual Entry, and the
  // reason only when that category is `other`.
  | 'reason_category_required'
  | 'idempotency_key_required'
  | 'unsupported_media_type'
  | 'file_too_large'
  // 403
  | 'forbidden'
  | 'not_authorized_for_step'
  | 'no_document_access'
  | 'not_visible'
  // 404
  | 'not_found'
  // 409
  | 'version_conflict'
  | 'blocked_by_hold'
  | 'out_of_order'
  | 'duplicate_key'
  | 'already_resolved'
  // §5.2 (owner, 2026-09-25 — `docs/decisions.md` D-149): Mark Resolved on an
  // `auto` flag whose condition does not hold yet — the field is still blank,
  // the amounts still do not reconcile, a tag is still unmatched. The app
  // disables the button until the condition holds; this is the enforcement.
  | 'flag_condition_unmet'
  | 'already_released'
  | 'idempotency_key_reuse'
  | 'export_in_progress'
  | 'unresolved_flags'
  | 'amounts_do_not_reconcile'
  // The third Import Control release gate (PM ruling): a line item with no
  // ACCOUNT blocks release. It is keyed on the account and not on the
  // composed code deliberately — the department is not knowable at Import
  // Control, and gating on the composed code there would refuse release on the
  // 73% of the chart whose accounts admit more than one department. The
  // composed code is gated later instead, at every approval tier, at Manual
  // Entry and at export.
  | 'line_item_without_account'
  // Import Control's release and Controller Approval's approve, the two steps
  // that can still change an Account: an active line holds an Inactive one
  // (owner, 2026-10-07 — D-308). Past them the Account is locked and the
  // invoice proceeds as it is.
  | 'line_item_inactive_account'
  // Import Control's release and Denial Review's reinstate, the two steps that
  // can still change the Vendor: the invoice's Vendor is Inactive (code review
  // G-09, 2026-10-17). Past them the Vendor is locked and the invoice proceeds.
  | 'inactive_vendor'
  // The invoice-date release gate (owner item 5, 2026-08-28): `invoice_date` is
  // null or blank. Its own code rather than riding `unresolved_flags`, because
  // the `MissingInvoiceDate` flag only exists on an invoice that ARRIVED
  // without a date — a reviewer who clears a date that was extracted leaves no
  // flag behind, and this is the refusal that stops them.
  | 'missing_invoice_date'
  // Approval, Manual Entry: an active line still has no department. Nobody
  // outside the organization can supply this, so it is asked of the approver.
  | 'line_item_without_department'
  | 'line_item_without_marketing_category'
  | 'missing_required_field'
  | 'line_item_limit_reached'
  | 'conflict'
  // 422 — the composition invariant. A department that forms no real code
  // with the line's account, checked against the row AS READ inside the
  // transaction. Its own code rather than a plain `validation_failed` because it
  // is the one refusal a client can act on mechanically: `details.line_numbers`
  // names the offending lines, so the bulk control can deselect exactly them.
  | 'does_not_compose'
  // 422 — item 8's floor. `88-880` and `88-885` compose only with department
  // `190110`, so a line under the $5,000 floor may not be coded to either
  // account, not merely denied the department — otherwise the line would have
  // no department it could ever legally take (D-9). Carries
  // `details.line_numbers` like `does_not_compose` does. `POST /codes`
  // (owner item 5, 2026-08-19) throws this SAME code, with no line numbers,
  // when a new code would give `88-880`/`88-885` a second composing
  // department other than `190110` — the code-creation twin of this refusal,
  // since a client handles one 422 spelling of the floor rather than two.
  | 'capital_equipment_minimum'
  // 422 — the invoice report's size cap (owner, 2026-09-10). More than
  // `REPORT_INVOICE_CAP` invoices matched the criteria. Its own code rather
  // than a plain `validation_failed` because the client acts on it
  // mechanically: `details.count` and `details.cap` are what the screen needs
  // to say "412 invoices match; narrow it to 250 or fewer". A report is refused
  // WHOLE rather than truncated — a listing that silently stops at 250 of 412
  // is worse than no listing — and nothing is written when it is refused: no
  // audit row, no presigned URL.
  | 'report_too_large'
  // 413 / 429 / 500 / 503
  | 'payload_too_large'
  | 'rate_limited'
  | 'internal_error'
  | 'dependency_unavailable'

export interface FieldError {
  field: string
  code: string
  message: string
}

/**
 * Every 4xx/5xx body. `message` is human text safe to display and NEVER contains
 * PHI — no document content, no extracted field values, no person's name beyond
 * what the caller already supplied.
 */
export interface ApiError {
  error: {
    code: ErrorCode
    message: string
    details?: Record<string, unknown> & { errors?: FieldError[] }
    request_id: string
  }
}

// ---------------------------------------------------------------------------
// 1. Session and profile — GET /me
// ---------------------------------------------------------------------------

export interface MeAssignment {
  id: Uuid
  workspace: WorkspaceKind
  role: AssignmentRole
  /**
   * Whether this seat is about ONE company or the whole organization (D-134,
   * 2026-09-22). `organization` is the Oversight seat and nothing else.
   */
  company_scope: AssignmentCompanyScope
  /**
   * The company this seat is about, or `null` when `company_scope` is
   * `organization`. Also `null` on a retired, pre-2026-08-10 oversight row
   * surfacing through assignment history, which is org-wide too.
   */
  company_id: string | null
  company_name: string | null
  /**
   * THE ORG-WIDE EXPANSION. The companies this one seat covers for DECIDING and
   * for SEEING a record — every company for an `organization`-scoped seat, and
   * empty for a company-scoped one, which covers the company it names.
   *
   * IT USED TO BE FILLED BY A MAGIC COMPANY ID — `All`, and before that `Other`
   * with its 21 catch-all members. D-134 (2026-09-22) removed both: the reach
   * comes from `company_scope` now, and no company id expands.
   *
   * It does NOT extend appointment authority, which stays literal `company_id`
   * equality (the 2026-08-03 broker ruling).
   */
  covers_company_ids: string[]
}

interface MeCapabilities
{
  /**
   * The document-access policy. True for the internal roles and for `admin`, and
   * for a holder of an active assignment in ANY of the three company approval
   * tiers — `department-head`, `director` and `oversight` alike (owner ruling
   * R2, 2026-08-10: PDF preview stays granted to all three, exposure accepted —
   * see docs/decisions.md).
   */
  can_preview_pdf: boolean
  /**
   * May the caller open the Assignments tab at all — any role or any active
   * assignment, not just admin and directors.
   */
  can_view_assignments: boolean
  /**
   * WHICH References tabs this caller may open, in tab order (owner item 29,
   * 2026-08-28) — the server's answer, computed by `referenceSectionsFor` in
   * `backend/src/domain/authority.ts`. An EMPTY array means the References
   * screen must not be offered at all.
   *
   * This is a HINT for the navigation, never the boundary: every write route
   * re-asks the same question through `canManageReferenceSection`, so a hidden
   * tab and a refused write are two independent facts and only the second one
   * is security.
   */
  reference_sections: ReferenceSection[]
  /** The same, for the Rules screen (`rulesSectionsFor`). Empty means no Rules
      screen. */
  rules_sections: RulesSection[]
  /** The same, for the Analytics screen (`analyticsSectionsFor`, owner
      2026-09-10). Empty means no Analytics tab. A tab is offered when the caller
      holds at least one of its appointments (D-258). */
  analytics_sections: AnalyticsSection[]
  /** The companies each Analytics tab's appointments name (D-258): what the tab
      shows, and what its Company filter offers. Empty for a tab not held. */
  analytics_companies: Record<AnalyticsSection, string[]>
  /** What row-level security reads as `app.is_internal`. */
  is_internal: boolean
}

export interface MeResponse {
  user_id: Uuid
  upn: string
  display_name: string
  /**
   * The address `conversations.mentions` stores, so the "mentioned you" indicator
   * compares against it and not the UPN.
   */
  directory_email: string
  workspaces: WorkspaceKind[]
  roles_by_workspace: Record<string, UserRole[]>
  assignments: MeAssignment[]
  /**
   * EXPANDED — every company a director seat reaches for DECIDING and SEEING,
   * including the `Other` anchor's member companies.
   *
   * This is the list a client must test a record's `company_id` against before
   * showing or hiding an approve control. Testing the literal list instead hides
   * the control on a member company's invoice that the API would accept — wrong in
   * the safe direction, but still wrong, and the record then looks unactionable to
   * the only person who can act on it.
   */
  director_covered_companies_by_workspace: Record<string, string[]>
  /** EXPANDED, as above, for department-head seats. */
  department_covered_companies_by_workspace: Record<string, string[]>
  /** EXPANDED, as above, for `controller` seats (D-303). */
  controller_covered_companies_by_workspace: Record<string, string[]>
  /** EXPANDED, as above, for `oversight` seats — the list `canDecide`'s Oversight
      arm must test a record's `company_id` against, exactly like the director and
      department-head pairs above. */
  oversight_covered_companies_by_workspace: Record<string, string[]>
  /**
   * Informational for UI copy. Row-level security is the boundary, not this list.
   * Derived exactly as `contextFor` derives the database's company list — catch-all
   * expansion included, Analytics seats excluded — so the two cannot disagree.
   */
  visible_company_ids: string[]
  capabilities: MeCapabilities
}

// ---------------------------------------------------------------------------
// 2. Stuck tiers
// ---------------------------------------------------------------------------

/**
 * The nodes whose progress depends on somebody holding a company-scoped
 * assignment — so the only nodes that can FREEZE for want of a seat.
 *
 * The four approval tiers (Controller since 2026-10-07, D-303). `ap-ar` was
 * one more from issue 3 (2026-08-11)
 * until AP/AR Approval was merged into Import Control (owner, 2026-09-29 —
 * D-215); Import Control is authorized by a user role, so it cannot freeze for
 * want of a company seat. The name is `STAFFED` rather than `TIER` because it
 * names what each node waits for: a seat. Since D-336 no step is skipped for
 * want of one, so an invoice at any of the four waits there until it is filled.
 *
 * `satisfies readonly NodeKey[]` is load-bearing: `Extract<NodeKey, …>` alone
 * collapses to `never` if a node key is renamed, and `Partial<Record<never, T>>`
 * is `{}`, so `tsc` would pass while the payload silently lost every node.
 */
export const STAFFED_NODE_KEYS = ['controller', 'department', 'director', 'oversight'] as const satisfies
  readonly NodeKey[]
export type StaffedNodeKey = (typeof STAFFED_NODE_KEYS)[number]

export interface StuckNodeCompany {
  company_id: string
  company_name: string
  count: number
}

/** Named for the NODE, not for a tier. Renamed from `StuckTier` with issue 3. */
export interface StuckNode {
  /** Invoices parked at this node with nobody assigned for their company. */
  count: number
  /** The companies they belong to, count-desc then company_name-asc. */
  companies: StuckNodeCompany[]
}

/**
 * `GET /queues/stuck` — the diagnostic behind the Pipeline dialog's warning
 * icon, fetched lazily on dialog open (`docs/api.md` §2.2).
 */
export interface QueueStuckResponse {
  /** A node with nothing stuck is omitted — an empty entry says nothing worth a key. */
  stuck: Partial<Record<StaffedNodeKey, StuckNode>>
  as_of: Timestamp
}

// ---------------------------------------------------------------------------
// 3. Invoices
// ---------------------------------------------------------------------------

export interface Invoice {
  id: Uuid
  record_type: WorkspaceKind

  /**
   * Which system the invoice came from (D-380). `accounts_payable` for every
   * invoice read in here; anything else is LOCKED — only Vendor and each
   * line's Account and Department can change (`isLockedSource`).
   */
  source_system: SourceSystem
  /** The invoice's id in that system; `null` exactly when `source_system` is `accounts_payable`. */
  source_record_id: string | null

  external_invoice_id: string | null
  upload_id: Uuid
  uploader_email: string | null
  uploaded_at: Timestamp | null

  /**
   * THE invoice's company, `invoices.company_id` — one per invoice, before and
   * after release (owner item 5, 2026-08-17; code review B-05, 2026-10-03).
   * Every screen reads this: the editor, the queue card, the read-only views,
   * the company filter. NOT the row-level-security partition, which is
   * server-side only and is copied from this at release. Null until a company
   * is set (`MissingCompany`).
   */
  company_id: string | null
  /** The matched master company's name, beside `company_id`. Never the printed
      bill-to, which is `company_name_as_printed`. */
  company_name: string | null
  company_source: CompanySource | null
  company_name_source: CompanyNameSource | null
  company_name_as_printed: string | null

  vendor_id: string | null
  vendor_name: string | null
  /** Written once at extraction. The vendor-precedent and alias match key. */
  vendor_name_as_printed: string | null

  invoice_date: DateOnly | null
  /** DERIVED (owner, 2026-08-14 — `docs/decisions.md` D-3): `SUM` of the active
      line items' totals, written by every path that writes lines. A display
      figure: the reconcile test is lines + tax + shipping against `total`
      (D-151), and the subtotal is not an input to it. */
  subtotal: MoneyString | null
  /** Not a line item and it carries no GL code. Spread proportionally across
      the lines only when the Point Click Care CSV is built. A NULL tax counts
      as zero, so it cannot hide a gap between the lines and the total. */
  tax: MoneyString | null
  /** The second header charge, in every respect `tax`'s peer: as printed, never
      on a line item, spread across the GL codes by its own allocation at
      CSV-build time, and a NULL counts as zero. An itemized freight LINE stays a
      line item — `docs/decisions.md` D-1. */
  shipping: MoneyString | null
  /** As printed. The reconcile test: lines + tax + shipping within three cents
      of it (D-151) — the export guard's own test. */
  total: MoneyString | null
  /** Evidence only (item 2, 2026-08-16): the net amount already owed from
      before this billing period. Never in `subtotal`, never in `total`,
      never exported — Point Click Care already holds it. Non-null and
      non-zero unconditionally raises `PriorBalanceIncluded`. */
  prior_balance: MoneyString | null
  currency: string
  document_type: DocumentType | null

  status: InvoiceStatus
  last_status_change_at: Timestamp | null

  import_control_decision_id: Uuid | null

  /** RETIRED pointer (D-215): nothing writes it since AP/AR Approval was merged
      into Import Control. Set only on an invoice released at AP/AR before then. */
  ap_ar_approval_decision_id: Uuid | null
  /** Set by Controller Approval's own decision (D-303). Non-null means the
      invoice went through Controller Approval, which is what locks its Account
      and Department after that step (D-305). */
  controller_approval_decision_id: Uuid | null
  department_approval_decision_id: Uuid | null
  /** The legacy `pcs_primaryapprovaldecision` slot, renamed. */
  director_approval_decision_id: Uuid | null
  /** The legacy `pcs_secondaryapprovaldecision` slot, renamed. */
  oversight_approval_decision_id: Uuid | null
  export_control_decision_id: Uuid | null
  denial_review_decision_id: Uuid | null

  export_id: Uuid | null

  /** THE MANUAL-ENTRY TAG: true once Export Control's Typed Into
      Point Click Care has been submitted (D-311). Earlier it was a request for
      the Manual Entry step, which that change dropped. */
  manual_entry_requested: boolean
  /** The tag's mirror, equal to `manual_entry_requested`. It was the routing
      to the Manual Entry step until that step was dropped (D-311). */
  manual_entry: boolean

  line_items_capped: boolean

  /**
   * THE ROUTE THE RELEASE OR REINSTATE DECIDED (owner, 2026-10-12 — D-350:
   * "Routing rules should only get checked at Release and Reinstate"). Every
   * approval after it follows this route to Export Control; nothing asks the
   * rules again until the next reinstate. Null before the first release.
   */
  route: InvoiceRoute | null
  /**
   * An Override set in the Override pop-up and not yet applied (D-350). The
   * next release or reinstate applies it, writes the vendor's rule when
   * `create_rule` is checked, and clears it. Null when none is set.
   */
  route_override: RouteOverride | null

  ai_extracting_confidence: Confidence | null
  /**
   * The AI Notes are three, one per pass, shown as three bullets in pipeline
   * order (D-250): `ai_splitting_notes` (where this invoice starts and ends),
   * `ai_extracting_notes` (reading it), `ai_matching_notes` (recognising and
   * coding it). `ai_splitting_notes` is null on an invoice extracted before
   * 2026-09-30, whose splitting note was merged into `ai_extracting_notes`.
   */
  ai_splitting_notes: string | null
  ai_extracting_notes: string | null
  ai_vendor_match_confidence: Confidence | null
  ai_company_match_confidence: Confidence | null
  ai_matching_notes: string | null
  ai_matching_model: string | null
  ai_matching_input_tokens: number | null
  ai_matching_output_tokens: number | null

  upload_page_start: number | null
  upload_page_end: number | null

  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number

  // Derived, read-only.
  total_flag_count: number
  /** Zero is the release gate. */
  unresolved_flag_count: number
  /** Active line items still missing "What is it?" (the account) or "Who is
      it for?" (the department). Zero is what lets an approval queue OFFER
      Approve (`docs/decisions.md` D-9) — the server's gate in
      `routes/decisions.ts` is what enforces it. */
  uncoded_line_item_count: number
  /** Active line items coded to the Capital Equipment department (`190110`)
      whose total is null or under the $5,000 floor. These lines are FULLY
      coded (both halves set), so `uncoded_line_item_count` reads 0 for them
      and cannot be the client's signal to withhold Approve. Zero is what lets
      Approve stay offered (`docs/decisions.md`, the entry superseding D-18 in
      part, owner item 14) — the server's gate in `routes/decisions.ts` is
      still what enforces it. */
  capital_equipment_under_floor_count: number
  /** Active line items whose settled account carries
      `detail_kind = 'marketing'` and which still have no Marketing category
      (owner, 2026-09-15). These lines are FULLY GL-coded, so
      `uncoded_line_item_count` reads 0 for them — the same reason
      `capital_equipment_under_floor_count` above is its own number. Zero is
      what lets an approval queue offer Approve; the server's gate in
      `routes/decisions.ts` is still what enforces it. */
  missing_marketing_category_count: number
  has_active_hold: boolean
  /** The active hold, or null — what a queue needs to show who held the
      invoice and why, and to release it, without a read per held row
      (D-284). Null exactly when
      `has_active_hold` is false. */
  active_hold: ActiveHold | null
  /** Unresolved flag types only. */
  flag_types: string[]
  /** From `last_status_change_at`, falling back to `updated_at`. */
  age_days: number | null
}

export type InvoiceSort =
  | 'invoice_date_asc'
  | 'invoice_date_desc'
  | 'last_status_change_at_desc'
  | 'total_desc'

export type InvoiceInclude =
  | 'line_items'
  | 'flags'
  | 'decisions'
  | 'holds'
  | 'associated_entities'
  | 'conversations'
  /** The invoice's own Total changes, for the modal's History (D-329). */
  | 'total_corrections'
  /** The upload the invoice came from, for History's first row (D-330). */
  | 'upload'
  /** The notifications sent about the invoice's upload (D-335) and about the invoice
      itself (D-336), for History. */
  | 'notifications'

/**
 * A NOTIFICATION SENT ABOUT THE INVOICE'S UPLOAD, OR ABOUT THE INVOICE ITSELF, AS
 * HISTORY READS IT (owner, 2026-10-08 — D-335: *"Can we also add "Alert sent to
 * x, y, and z" actions to the timeline? The finer text underneath the action
 * can name the alert."*). One per notification sent: every recipient of the upload's
 * notification of that type, or of one Invoice Passed Without Any Approvals notification
 * (D-336), which is about one decision on this invoice.
 * Names, addresses, the type and the time ONLY — never the subject or a body,
 * which carry the upload's details.
 */
export interface InvoiceNotificationSummary {
  notification_type: NotificationType
  /** The earliest `sent_at` among its rows. */
  sent_at: Timestamp
  /** In the order the rows were written. `name` is null when none is known. */
  recipients: { name: string | null; email: string }[]
}

/**
 * THE UPLOAD, AS HISTORY'S FIRST ROW READS IT (owner, 2026-10-08 — D-330):
 * *"Uploaded by <name>"* with the file and its pages, or *"Uploaded by email
 * from <address>"*. The narrowest view of `uploads` the row needs, readable by
 * anyone who can read the invoice. The file's name is shown only to a caller
 * with document access (`canPreviewPdf`), the same rule that gates the upload
 * record and its PDF (`docs/security.md` §5); everyone else gets null.
 */
export interface InvoiceUploadSummary {
  /** `app` when a signed-in person uploaded it in the app (`uploads.uploaded_by`
      is set); `email` otherwise — mail intake, a scanner, a service relay, and
      any upload registered before 2026-10-07, when that column was added. */
  channel: 'app' | 'email'
  uploaded_at: Timestamp | null
  /** The uploader's display name, recorded at upload. Null for `email`, and
      for an in-app upload registered before 2026-10-08. */
  uploaded_by_name: string | null
  /** `uploads.source`: the uploader's address for `app`, the sender's for
      `email`. Already on the invoice as `uploader_email`. */
  from_address: string | null
  /** Null without document access, and when the upload recorded none. */
  file_name: string | null
  /** The file's page count as the extract worker counted it; null when it could not. */
  page_count: number | null
}

export interface InvoiceDetailResponse {
  invoice: Invoice
  line_items?: InvoiceLineItem[]
  flags?: Flag[]
  decisions?: Decision[]
  holds?: Hold[]
  associated_entities?: AssociatedEntity[]
  conversations?: Conversation[]
  /** Header `total` corrections only (`field = 'total'`, no line item), oldest
      first — what History shows as "Total changed by …" (D-329). */
  total_corrections?: Correction[]
  /** With `include=upload`; null when the upload row cannot be found. */
  upload?: InvoiceUploadSummary | null
  /** With `include=notifications`, oldest first (D-335). */
  notifications?: InvoiceNotificationSummary[]
}

// ---------------------------------------------------------------------------
// 4. Line items
// ---------------------------------------------------------------------------

export interface InvoiceLineItem {
  id: Uuid
  invoice_id: Uuid
  /** Was `pcs_invoicesequence`: the line's identity within the invoice. */
  line_number: number

  /** Always the invoice's own `source_system` (D-380). */
  source_system: SourceSystem
  /** The line's id in that system; `null` exactly when `source_system` is `accounts_payable`. */
  source_record_id: string | null

  /** Denormalized from the header, which is the writer. */
  vendor_id: string | null

  // NO COMPANY. A line has none of its own: the invoice's is
  // `Invoice.company_id` (code review B-05, 2026-10-03).

  /**
   * The composed code, `department_id || '-' || account_id`.
   *
   * READ-ONLY ON THE WIRE. It is a stored generated column in Postgres, so no
   * request body carries it and no writer sets it; it appears exactly when both
   * halves do.
   */
  final_code_id: string | null
  code_name: string | null

  /** "What is it?" — on the document, settled at Import Control. */
  account_id: string | null
  account_source: AccountSource | null
  /** "Who is it for?" — never on the document, settled at Department Approval or later. */
  department_id: string | null
  department_source: DepartmentSource | null

  printed_code: string | null

  /** Editable at Import Control — which is why it is not the precedent key. */
  description: string | null
  /** Written once at extraction, never overwritten. THE GL-precedent match key. */
  description_as_printed: string | null
  quantity: QuantityString | null
  /** Exact, at any scale (D-249). `total` is derived from it and is whole cents. */
  unit_price: PriceString | null
  /**
   * The PRE-TAX extended amount as printed, so `SUM(total)` equals the invoice
   * SUBTOTAL, not the total (2026-08-11 — it was tax-inclusive before).
   *
   * Tax is not a line item and gets no GL code. The invoice's tax is spread
   * across the GL codes in proportion to their totals only when the Point Click
   * Care CSV is built, so the amount POSTED to a GL code is larger than the
   * sum of the line items rolled up into it. Do not add tax here.
   */
  total: MoneyString | null

  ai_extracting_confidence: Confidence | null
  ai_extracting_notes: string | null
  /** Pass 2 scores the ACCOUNT. It never proposes a department. */
  ai_account_match_confidence: Confidence | null
  ai_matching_notes: string | null

  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number

  /**
   * Owner item 11 (2026-08-19). Present (possibly `null`) on every line;
   * whether the line NEEDS a Marketing Category is decided by its own
   * `account_id` resolving to `detail_kind: 'marketing'` on the accounts
   * master (`GET /accounts`), not by whether this is `null`. Editable wherever
   * the Account is (D-192, `backend/src/domain/authority.ts`
   * `canEditLineItemDetail`).
   */
  marketing_detail: LineItemMarketingDetail | null
  /**
   * Fleet Maintenance's own detail for the line (D-381): present on a line
   * copied in from Fleet Maintenance, `null` on every other. Read-only — no
   * request body carries it, and nothing in this app requires it.
   */
  transportation_detail: LineItemTransportationDetail | null
}

/**
 * The Marketing sub-form's stored state for one line item. `category` /
 * `category_context` are resolved at READ TIME by a join onto
 * `marketing_categories` — never a stored copy, so a category rename or
 * retire never strands stale text (the same discipline `docs/api.md`
 * §15.13–15.15 already applies to `coding_rules.vendor_name`).
 */
export interface LineItemMarketingDetail {
  category_id: Uuid | null
  /** `null` when `category_id` is `null`, or when it no longer resolves (a retired/renamed row is still a valid foreign key). */
  category: string | null
  category_context: string | null
  row_version: number
}

/**
 * Fleet Maintenance's detail for one line, AS IT SENT IT (owner, 2026-10-19 —
 * D-381). Copied verbatim and never edited here: `category` and `action` are
 * Fleet Maintenance's own words ("Brakes", "REPLACE"), with no vocabulary table
 * behind them. `parts_cost` and `labor_cost` break down the line's total;
 * nothing here adds them up or checks them against it.
 */
export interface LineItemTransportationDetail {
  category: string | null
  action: string | null
  parts_cost: MoneyString | null
  labor_cost: MoneyString | null
  /** Decimal string, e.g. `"1.5"`. */
  labor_hours: string | null
  notes: string | null
  row_version: number
}

/** A line item in an intake-save `upsert` array. No `id` means create. */
interface InvoiceLineItemUpsert {
  id?: Uuid
  row_version?: number
  line_number: number
  /** The intake save is Import Control's, the one step that edits invoice values. */
  account_id?: string | null
  /**
   * Required at Import Control's release since D-309 (optional from D-217 until
   * then): a value equal to the stored one is ignored — unless it is sent
   * beside a new account it fits, when it is kept (D-229) — and a different one
   * must compose with the account the line will hold — see `docs/api.md` §3.3.
   */
  department_id?: string | null
  /**
   * COMBINE INTO ONE LINE (D-230), on a NEW line only: the saved lines this one
   * replaces. Each must also be in `remove`. Their tags move to the new line,
   * one per entity, and their Marketing Category carries where every one of
   * them agrees.
   */
  combines?: Uuid[]
  /**
   * THE COMBINED LINE'S ADDITIONAL DETAIL (D-242), beside `combines` only: what
   * the reviewer chose in the bulk row. A field sent replaces the carried value
   * (`null` blanks it); a field left out keeps it. The Marketing Category lands
   * only on a Marketing account.
   */
  marketing_category_id?: Uuid | null
  description?: string | null
  quantity?: QuantityString | null
  /** Kept exactly (D-249); at most 10 decimal places and 15 digits. */
  unit_price?: PriceString | null
  /** IGNORED when stored: the server derives the total from the exact price x
      quantity, rounded once to the cent (D-249). Sent for compatibility. */
  total?: MoneyString | null
  printed_code?: string | null
}

export interface InvoiceHeaderWrite {
  external_invoice_id?: string | null
  vendor_id?: string | null
  /** The invoice's one company; `null` clears it. A line takes no company. */
  company_id?: string | null
  invoice_date?: DateOnly | null
  /** Sent for completeness and to keep the payload self-describing, but the
      server RECOMPUTES it from the saved line items (D-3): whatever arrives here
      is overwritten by `SUM` of the active lines' totals. */
  subtotal?: MoneyString | null
  tax?: MoneyString | null
  shipping?: MoneyString | null
  total?: MoneyString | null
  currency?: string | null
  document_type?: DocumentType | null
}

/** The Import Control save — one composite transaction, five side effects. */
export interface IntakeSaveRequest {
  row_version: number
  invoice: InvoiceHeaderWrite
  line_items?: {
    upsert?: InvoiceLineItemUpsert[]
    /** Soft delete: `is_active = false`, `retired_at` stamped. Never physical. */
    remove?: Uuid[]
  }
  release?: boolean
  /** Where the Release was clicked, stored on the release's decision row
      (D-331). Client-reported; ignored without `release: true`. */
  decision_source?: DecisionSource | null
  /**
   * The Override pop-up's route (D-350): an object sets or replaces the
   * invoice's pending Override, `null` clears it, and leaving it out keeps
   * what is stored. Saved before a `release` in the same request, so a Release
   * clicked straight after the pop-up applies it.
   */
  route_override?: RouteOverrideWrite | null
}

export interface IntakeSaveResponse extends InvoiceDetailResponse {
  /** The status the release advanced to, or `null` when `release` was false. */
  released_to: InvoiceStatus | null
  corrections_written: number
}

export interface InvoiceLineItemCodeEditResponse {
  line_item: InvoiceLineItem
  /** Null when the value sent is the one the line already held: no correction is written (D-368). */
  correction_id: Uuid | null
}

// ---------------------------------------------------------------------------
// 5. Flags
// ---------------------------------------------------------------------------

export interface Flag {
  id: Uuid
  parent_id: Uuid
  record_type: WorkspaceKind
  flag_type: string
  /** Per-type context: the duplicate's id, the offending line number, a classification. */
  detail: string | null
  resolved_by_name: string | null
  resolved_by_email: string | null
  resolved_by_user_id: string | null
  /** A flag is resolved IFF this is non-null. */
  resolved_at: Timestamp | null
  is_active: boolean
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number

  // Joined from `flag_definitions`.
  label: string
  resolution: FlagResolution
  description: string
}

export interface FlagResolveResponse {
  flag: Flag
  unresolved_flag_count: number
}

// ---------------------------------------------------------------------------
// 6. Decisions
// ---------------------------------------------------------------------------

/**
 * One APPLIED decision. Every row in this collection succeeded: a refused attempt
 * writes nothing and is recorded in structured logs only, so there is no
 * `rejection_reason` field and no rejected row to filter out.
 */
export interface Decision {
  id: Uuid
  parent_id: Uuid
  record_type: WorkspaceKind
  decided_by_name: string | null
  decided_by_email: string | null
  decided_by_user_id: string | null
  decided_at: Timestamp
  /** A live step, or — on a row written before 2026-09-29 — the retired
      `AP/AR Approval Decision` (D-215). `RETIRED_DECISION_STEP_LABELS` names it. */
  step: RecordedDecisionStep
  decision: DecisionOutcome
  /** Required for every outcome except `approved`. */
  reason: string | null
  reason_category: ReasonCategory | null
  /** Where in the app the decision was taken (D-331): `bulk_queue`,
      `queue_row` or `modal`. CLIENT-REPORTED — a usage signal, not an audit
      fact. Null when none was sent, before 2026-10-08, and on a skip. */
  source: DecisionSource | null
  /** Every routing rule that matched when this decision routed the invoice —
      the release from Import Control or a reinstate from Denial Review — as
      it stood then (D-336). Null on every other decision, and when no rule
      matched and the invoice took the default path. */
  routing_rules: DecisionRoutingRule[] | null
  /** The route this decision decided (D-350): on a release or a reinstate
      from 2026-10-12, whatever decided it — an Override, the rules or the
      default route. Null on every other decision and before that date. */
  route: DecidedRoute | null
  is_active: boolean
  created_at: Timestamp
}

/**
 * The actor is the authenticated caller. A user id in the payload is rejected
 * with `invalid_request` — the source read the actor from the row's creator and
 * never from the payload, and that property must not weaken.
 */
export interface DecisionRequest {
  invoice_id: Uuid
  step: DecisionStep
  decision: DecisionOutcome
  reason?: string | null
  reason_category?: ReasonCategory | null
  row_version?: number
  /** Where the click was (D-331). Optional; validated against
      `DECISION_SOURCES`. Also accepted by `POST /decisions/batch`. */
  source?: DecisionSource | null
}

export interface DecisionResponse {
  decision: Decision
  invoice: Invoice
  previous_status: InvoiceStatus
  new_status: InvoiceStatus
}

export interface BatchDecisionItemResult {
  invoice_id: Uuid
  applied: boolean
  new_status?: InvoiceStatus
  error?: { code: ErrorCode; message: string }
}

/**
 * Always HTTP 200 with per-item results — a partial-success list is not an HTTP
 * error. `applied_count` is exactly the number of `decisions` rows written.
 */
export interface BatchDecisionResponse {
  results: BatchDecisionItemResult[]
  applied_count: number
  rejected_count: number
}

// ---------------------------------------------------------------------------
// 7. Holds
// ---------------------------------------------------------------------------

/** The active hold as an `Invoice` carries it (§3.1): the fields a held
    invoice's notice, tooltip and Release Hold use. */
export type ActiveHold = Pick<Hold, 'id' | 'held_by_name' | 'held_by_email' | 'held_at' | 'reason' | 'reason_category'>

export interface Hold {
  id: Uuid
  parent_id: Uuid
  record_type: WorkspaceKind
  /** The step the hold was placed at. Informational only. May be the retired
      `AP/AR Approval Decision` on a hold placed before 2026-09-29 (D-215). */
  step: RecordedDecisionStep | null
  held_by_name: string | null
  held_by_email: string | null
  held_by_user_id: string | null
  held_at: Timestamp
  /** Free text. Required when `reason_category` is `other` or absent; may be
      null on a hold whose named category is the whole explanation. */
  reason: string | null
  /** One of `HOLD_CATEGORIES` (owner, 2026-09-25). Null only on a hold placed
      before the Hold dialog asked for one. */
  reason_category: ReasonCategory | null
  released_by_name: string | null
  released_by_email: string | null
  released_by_user_id: string | null
  /** NULL = the hold is ACTIVE. Active = `is_active AND released_at IS NULL`. */
  released_at: Timestamp | null
  is_active: boolean
  created_at: Timestamp
  row_version: number
}

// ---------------------------------------------------------------------------
// 8. Conversations
// ---------------------------------------------------------------------------

export interface Conversation {
  id: Uuid
  parent_id: Uuid
  record_type: WorkspaceKind
  /** NEVER included in a notification email — a comment can hold patient information. */
  body: string
  author_name: string
  author_email: string
  /** Directory email addresses. Compare against the directory email, not the UPN. */
  mentions: string[]
  /** The step the invoice sat at when the comment was posted, stamped by the
      server (D-329). NULL at a status no step decides from, and on a comment
      posted before 2026-10-08. */
  step: RecordedDecisionStep | null
  created_at: Timestamp
}

export interface ConversationCreateResponse {
  conversation: Conversation
  notifications_queued: number
}

// ---------------------------------------------------------------------------
// 9. Associated entities
// ---------------------------------------------------------------------------

export interface AssociatedEntity {
  id: Uuid
  parent_id: Uuid
  /** The line item. NULL = a header-level reference. */
  child_id: Uuid | null
  record_type: WorkspaceKind
  entity_type: EntityType
  /** The document text that triggered the reference, verbatim. Read-only once saved. */
  entity_source_text: string | null
  entity_name: string | null
  vehicle_id: Uuid | null
  staff_upn: string | null
  match_source: MatchSource
  ai_extracting_confidence: Confidence | null
  ai_extracting_notes: string | null
  is_active: boolean
  created_at: Timestamp
  row_version: number
}

// ---------------------------------------------------------------------------
// 10. Uploads
// ---------------------------------------------------------------------------

/**
 * Pass 1's closed vocabulary for "these pages are not a payable document"
 * (`rejection_code` in `extraction.v1.json`). It chooses the note on the
 * record the host writes in that document's place; it is not stored and
 * refuses nothing (code review A-06, 2026-10-03).
 */
export type UploadRejectionCode = 'no_payable_document' | 'unreadable' | 'not_a_document'

export interface Upload {
  id: Uuid
  source: string | null
  uploaded_at: Timestamp | null
  processed_at: Timestamp | null
  status: UploadStatus
  /**
   * Where the pipeline has got to, or null (owner, 2026-09-03). Null means
   * queued while `status` is `Uploaded`, and done once `status` is terminal —
   * see `UPLOAD_PHASE`. Written by the `extract` and `match` workers only.
   */
  processing_phase: UploadPhase | null
  record_count: number | null
  record_type: WorkspaceKind
  expected_type: WorkspaceKind | null
  ai_extracting_model: string | null
  ai_extracting_input_tokens: number | null
  ai_extracting_output_tokens: number | null
  file_name: string | null
  object_key: string | null
  object_prefix: string | null
  /** Only a stable canonical URI. Presigned URLs are per request, never stored. */
  file_url: string | null
  /**
   * The company the uploader picked on the Upload form, applied to every
   * document extracted from this file (owner item 21, 2026-08-31). Null for
   * mail intake, for a scanner, and whenever the uploader left the picker
   * empty. Echoed back so the Upload screen can confirm what was recorded.
   */
  selected_company_id: string | null
  is_active: boolean
  created_at: Timestamp
}

export interface UploadPresignResponse {
  upload_id: Uuid
  object_key: string
  url: string
  fields: Record<string, string>
  expires_at: Timestamp
}

export interface PdfUrlResponse {
  url: string
  expires_at: Timestamp
  file_name: string
}

// ---------------------------------------------------------------------------
// 11. Exports
// ---------------------------------------------------------------------------

/**
 * One company's file within a batch, as it stands NOW. `file_name` and
 * `row_count` are the current version's: the original at `version` 1, or the
 * latest replacement Closing Confirmation's Typed Into Point Click
 * Care wrote (`docs/export.md`, Replacement files). `withdrawn` means every
 * invoice in the file was manually entered, so there is no current file to
 * download; the name is then the last file's.
 */
export interface ExportFile {
  file_id: Uuid
  file_name: string
  company_id: string
  company_name?: string
  row_count: number
  version: number
  withdrawn: boolean
  download_url?: string
  expires_at?: Timestamp
}

/**
 * One replacement of a company's file, from `export_file_replacements` — the
 * audit record. Written once, never changed. `file_name` and `row_count` are
 * null when the replacement withdrew the file.
 */
export interface ExportFileReplacement {
  id: Uuid
  file_id: Uuid
  company_id: string
  version: number
  invoice_id: Uuid
  decision_id: Uuid
  previous_file_name: string
  file_name: string | null
  row_count: number | null
  withdrawn: boolean
  replaced_by_name: string | null
  replaced_by_email: string | null
  replaced_at: Timestamp
  reason: string | null
  reason_category: ReasonCategory | null
}

export interface ExportBatch {
  id: Uuid
  exported_by_name: string | null
  exported_by_email: string | null
  exported_at: Timestamp
  status: ExportStatus
  record_count: number | null
  record_type: WorkspaceKind
  object_key: string | null
  is_active: boolean
  created_at: Timestamp
  files?: ExportFile[]
}

export interface ExportCreateResponse {
  export: ExportBatch
  files: ExportFile[]
  invoices_advanced: number
  /** The requested invoices left out because they are on hold (D-247). They
      are not in any file and stay at `Pending Export Control Decision`. */
  held_invoice_ids: Uuid[]
}

export interface ExportDetailResponse {
  export: ExportBatch
  files: ExportFile[]
  /** What drives Closing Confirmation's all-or-nothing view and the `Completed` flip. */
  invoice_counts: Partial<Record<InvoiceStatus, number>>
  /** Every file replacement on the batch, oldest first. */
  replacements: ExportFileReplacement[]
}

export interface DownloadUrlResponse {
  url: string
  expires_at: Timestamp
  file_name: string
}

// ---------------------------------------------------------------------------
// 12. Assignments
// ---------------------------------------------------------------------------

export interface Assignment {
  id: Uuid
  workspace: WorkspaceKind
  action: AssignmentAction
  /** A row whose stored role the code does not know is never returned (D-293). */
  role: AssignmentRole
  /**
   * ONE company (`company`) or the whole organization (`organization`), D-134,
   * 2026-09-22. Server-decided from `role` — `oversight` is org-wide and every
   * other role is company-scoped — so it is not part of the create request.
   */
  company_scope: AssignmentCompanyScope
  /** The company, or NULL when `company_scope` is `organization`. */
  company_id: string | null
  company_name: string | null
  user_id: string
  user_upn: string
  user_name: string | null
  target_assignment_id: Uuid | null
  /** Stamped on the retired APPOINT row. NULL = still active. */
  retired_at: Timestamp | null
  request_status: AssignmentRequestStatus
  requested_by_name: string | null
  requested_by_upn: string | null
  is_active: boolean
  created_at: Timestamp
}

export interface AssignmentCreateResponse {
  assignment: Assignment
  /** The appointment this request retired, when `action` was `retire`. */
  retired: Assignment | null
}

// ---------------------------------------------------------------------------
// 13. User roles
// ---------------------------------------------------------------------------

export interface UserRoleGrant {
  id: Uuid
  /** Entra ID object id — the stable identity. */
  user_object_id: Uuid
  user_upn: string
  user_email: string
  user_name: string | null
  workspace: WorkspaceKind
  role: UserRole
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

// ---------------------------------------------------------------------------
// 14. Reminder subscriptions
// ---------------------------------------------------------------------------

export interface ReminderSubscription {
  id: Uuid
  subscriber_id: string
  subscriber_name: string | null
  subscriber_email: string
  title: string | null
  days_of_week: DayOfWeek[]
  time_of_day: TimeOfDay
  workspaces: WorkspaceKind[]
  /** The hyphenated tokens only. `primary` / `secondary` are not accepted. */
  steps: PipelineStepKey[]
  status: SubscriptionStatus
  /** Shared by every version of one subscription (the first version's id). */
  lineage_id: Uuid
  superseded_at: Timestamp | null
  /** Per-ET-day send dedup. CARRIED FORWARD on edit, so a mid-day edit cannot double-send. */
  last_sent_at: Timestamp | null
  is_active: boolean
  created_at: Timestamp
  row_version: number
}

export interface ReminderSubscriptionUpdateResponse {
  subscription: ReminderSubscription
  superseded_id: Uuid
}

// ---------------------------------------------------------------------------
// 15. References — master data
// ---------------------------------------------------------------------------

export interface Company {
  id: Uuid
  company_id: string
  company_name: string
  is_active: boolean
  retired_at: Timestamp | null
  /** The uploader-domain map, shown on the row. A company may have several. */
  email_domains: string[]
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
}

export interface CompanyEmailDomain {
  id: Uuid
  /**
   * THE match key, lowercased: a whole domain with its leading `@`
   * (`@example.com`), or since D-379 one full address (`name@example.com`),
   * which wins over its domain. The column keeps its old name.
   */
  domain: string
  company_id: string
  company_name: string | null
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}


/**
 * An email address allowed to mail documents into intake. The gate is data:
 * the ingest branch unions this table with the env-config floor, so admitting
 * a sender is a row, not a deploy. No company mapping — where a document files
 * is the domain map's business.
 */
export interface IntakeSender {
  id: Uuid
  /**
   * Lowercased, trimmed. Either `name@example.com` (one sender, exact) or
   * `@example.com` (any sender on the domain — the company-domain-map
   * convention).
   */
  address: string
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

/**
 * An uploader rule's format default (owner, 2026-10-19 — D-379): every invoice
 * from this address or domain is recorded as `document_form`, whatever pass 1
 * guesses. No row means the format is interpreted: pass 1's guess (D-378).
 */
export interface SenderFormatDefault {
  id: Uuid
  /** Lowercased, trimmed: `name@example.com` (exact, wins) or `@example.com`. */
  key: string
  document_form: DocumentForm
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

/** One rule as it stands: its Active row on each of its three sides, if any. */
export interface SenderRule {
  key: string
  intake_sender: IntakeSender | null
  company_email_domain: CompanyEmailDomain | null
  sender_format_default: SenderFormatDefault | null
}

export interface Vendor {
  id: Uuid
  /** The code Point Click Care uses; it rides export column 2. */
  vendor_id: string
  vendor_name: string
  address_1: string | null
  address_2: string | null
  address_3: string | null
  city: string | null
  state_or_province: string | null
  /** Text — preserves leading zeros and ZIP+4. */
  postal_code: string | null
  phone: string | null
  fax: string | null
  /** The Remittance Address (D-241): Point Click Care's "Remit To Different
   *  Address" block. All six null means the vendor has none. */
  remittance_address_1: string | null
  remittance_address_2: string | null
  remittance_address_3: string | null
  remittance_city: string | null
  remittance_state_or_province: string | null
  remittance_postal_code: string | null
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
}

export interface Code {
  id: Uuid
  /** `NNNNNN-NN-NNN`. The same shape test decides whether a document token is a GL code. */
  code_id: string
  code_name: string
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
}

/**
 * Where a half row came from, and — the only thing that turns on it — who owns
 * its `is_active` (owner item 10, 2026-08-31, `docs/decisions.md` D-69).
 *
 *   * `derived` — the row exists because a code does. Every seeded row, and
 *     every row `POST /codes` upserts. `syncHalfActivityForCode` owns its
 *     `is_active`: active exactly when some active code references it.
 *   * `standalone` — authored in its own right through `POST /departments` /
 *     `POST /accounts`. The sync SKIPS it, so retiring the last code
 *     over it neither destroys it nor, on reactivation, resurrects one a person
 *     deliberately retired. Only its own `PATCH` changes `is_active`.
 */
export type HalfOrigin = 'derived' | 'standalone'

/**
 * The leading segment of a code — the department charged. "Who is it for?"
 *
 * Seeded as a DERIVATION of `codes` — every seeded row is the department
 * half of at least one code — but since owner item 10 (2026-08-31) a row may
 * also be authored on its own, ahead of any code that uses it; `origin` says
 * which, and is the only thing that decides who owns `is_active`.
 * `department_name` exists so a Facility Manager can answer without knowing GL coding;
 * `codes.code_name` stays authoritative, and what reaches Point Click Care is
 * the department id inside the code, never this name.
 */
export interface Department {
  id: Uuid
  /** `NNNNNN`. */
  department_id: string
  department_name: string
  /** Chart abbreviations and synonyms for type-to-search. Not displayed. */
  search_terms: string | null
  is_active: boolean
  origin: HalfOrigin
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
}

/**
 * The trailing two segments of a code — what was bought. "What is it?"
 *
 * The pair is inseparable: the object code is reused across expense classes, so
 * neither segment identifies anything alone.
 */
export interface Account {
  id: Uuid
  /** `NN-NNN`. */
  account_id: string
  account_name: string
  search_terms: string | null
  is_active: boolean
  origin: HalfOrigin
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
  /**
   * Owner item 11 (2026-08-19): `'marketing'` when a line coded to this account
   * needs the Marketing Category — set on `67-518` alone and `null` on every
   * other row. Data, not code: this column, not a hard-coded account id, is
   * what the app and the matching ladder read. No account carries
   * `'transportation'`: that detail arrives with a Fleet Maintenance line
   * (D-381).
   */
  detail_kind: LineItemDetailKind | null
}

/**
 * WHAT GOES INACTIVE WITH AN ACCOUNT, A DEPARTMENT OR A CODE (owner,
 * 2026-09-25 — `docs/decisions.md` D-175, D-180). Making one Inactive is never
 * refused for being in use; these records go Inactive with it, in the same
 * transaction. `GET /accounts/{account_id}/inactive-preview`, its Department
 * twin and `GET /codes/{code_id}/inactive-preview` answer with it before the
 * write, and the `PATCH` that makes the record Inactive answers with what it did
 * (`docs/api.md` §15.13, §15.13a). For a Code, `codes` is empty — the Code is
 * the subject — and the other lists hold the halves it leaves with no Active
 * Code and the Coding Rules that go with it. Every list is sorted. Invoices are
 * never touched, and a line holding one of these keeps it and still saves
 * (D-181). Making the record Active again makes none of these Active again.
 */
export interface InactiveCascade {
  /** Every Active Code built on the half. */
  codes: { code_id: string; code_name: string | null }[]
  /** An Account's knock-on: derived Departments whose last Active Code is in `codes`. */
  departments: { department_id: string; department_name: string }[]
  /** A Department's knock-on: derived Accounts whose last Active Code is in `codes`. */
  accounts: { account_id: string; account_name: string }[]
  /** Every Active Coding Rule naming the half or one of the knock-on halves. */
  coding_rules: {
    id: Uuid
    vendor_id: string | null
    vendor_name: string | null
    description_scope: string | null
    account_id: string | null
    department_id: string | null
  }[]
}

// ---------------------------------------------------------------------------
// Line item detail vocabulary — Marketing (owner item 11, 2026-08-19).
// Transportation's went on 2026-10-19 (D-381): Fleet Maintenance sends its own.
// ---------------------------------------------------------------------------

/**
 * A Marketing category CODE and what it means — the owner's own example: `A`
 * is the stored `category`, `"newspaper ad"` is `context`. UNLIKE `Department`
 * / `Account`, this table has a `POST` and a `PATCH` — it is not derived
 * from the chart, staff author it directly (`docs/api.md`
 * §15.13b).
 */
export interface MarketingCategory {
  id: Uuid
  category: string
  /** Plain English — what the code means. Rides into the pass-2 prompt. */
  context: string
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
}

/**
 * A standing coding decision for one vendor, optionally scoped to a line
 * description. Either half of a code, or both.
 *
 * `description_scope` is stored ALREADY NORMALIZED (lowercased, digits stripped,
 * whitespace collapsed — the same normalization GL precedent uses). NULL applies
 * to every line from the vendor and is the weakest match. How competing rules
 * resolve is one procedure, stated once, in "Rule resolution — the one
 * procedure" in `docs/schema.md` §3.9.
 */
export interface CodingRule {
  id: Uuid
  /**
   * NULL = a GENERAL rule (G1, owner 2026-09-10): it matches on the line's
   * description alone, whoever billed the invoice, and is the only rule rung an
   * invoice whose vendor did not resolve can reach. A general rule must carry a
   * `description_scope`.
   */
  vendor_id: string | null
  /**
   * The vendor's current name, resolved at READ TIME by a join onto `vendors`
   * — not stored. The join is on the `vendors` primary key and costs little
   * however many rows Memory adds (D-367), whereas a stored copy would
   * go stale on a vendor rename exactly like the two denormalized copies this
   * codebase already carries (`vendor_aliases.vendor_name`,
   * `company_email_domains.company_name`) — a real defect, not a hypothetical
   * one. `null` only if the row's `vendor_id` no longer resolves, which the
   * `vendors(vendor_id)` foreign key makes unreachable in practice.
   */
  vendor_name: string | null
  description_scope: string | null
  account_id: string | null
  department_id: string | null
  source: CodingRuleSource
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

// ---------------------------------------------------------------------------
// Oversight rules (owner, 2026-09-15). `docs/api.md` §21.
// ---------------------------------------------------------------------------

/**
 * One clause of a rule's sentence.
 *
 * EXACTLY ONE of the four value fields is populated, decided by the field's
 * type (`OVERSIGHT_FIELD_TYPE` in `shared/src/enums.ts`): `value_ids` for an
 * entity, `value_text` for text, `value_number` for money and number,
 * `value_date` for a date. The database refuses a row carrying two, because a
 * condition with two answers would be resolved by whichever the evaluator read
 * first.
 */
export interface OversightRuleCondition {
  /** Position in the rule's sentence, from 0. Unique within the rule. */
  position: number
  scope: OversightRuleScope
  field: OversightRuleField
  operator: OversightRuleOperator
  /**
   * Entity operators only — vendor codes, company ids, GL codes and the ids of
   * their halves. Several values are the condition's own internal `or`:
   * "company is A or B" is one condition, not two, which is what makes a rule
   * readable without a connector control.
   */
  value_ids: string[] | null
  value_text: string | null
  /** Money and number alike, as a decimal string like every other amount. */
  value_number: MoneyString | null
  value_date: DateOnly | null
  /**
   * Display names for `value_ids`, resolved at READ TIME by a join, in the same
   * index order — never stored. A stored copy would go stale on a vendor or
   * company rename, which is the defect `CodingRule.vendor_name` above records.
   * An entry is the stored code where the row no longer resolves, so a rule
   * naming a retired vendor still renders as a sentence rather than a blank.
   */
  value_labels: string[] | null
}

/**
 * The approval steps an invoice must pass, one flag per step (owner,
 * 2026-10-09 — D-336). A routing rule carries one set, an Override carries
 * one, and an invoice carries the one its release or reinstate decided
 * (D-350). With no Override and no rule matching, an invoice requires
 * Controller Approval and Director Approval (`DEFAULT_REQUIRED_APPROVALS`,
 * D-350). `requires_controller` and `requires_manager` are never both true:
 * they are one stage with two alternatives, and Controller wins when two rules
 * ask for both.
 */
export interface RequiredApprovals {
  requires_controller: boolean
  requires_manager: boolean
  requires_director: boolean
  requires_oversight: boolean
}

/** The route an invoice is on, and what decided it (D-350). */
export interface InvoiceRoute extends RequiredApprovals {
  source: RouteSource
}

/**
 * The Override pop-up's answer (owner, 2026-10-12 — D-350), as a save sends
 * it. The four steps are any combination but Controller Approval beside Manager
 * Approval, exactly as a rule's are. No category and no comment (D-350
 * amended: "It's a bit busy, and the CFO hasn't asked for justification behind
 * rules like these yet").
 */
export interface RouteOverrideWrite extends RequiredApprovals {
  /** "Remember this route for future invoices from this vendor." Checked by
      default. Ignored when the invoice has no vendor. */
  create_rule: boolean
}

/** A pending Override as stored on the invoice, with who set it and when. */
export interface RouteOverride extends RouteOverrideWrite {
  set_by_name: string | null
  set_by_email: string | null
  set_by_user_id: string
  set_at: Timestamp
}

/** What an Override did to the vendor's rule when it was applied (D-350). */
export type RouteOverrideRuleAction = 'created' | 'updated'

/** The route a release or reinstate decided, kept on its decision (D-350). */
export interface DecidedRoute extends InvoiceRoute {
  /** Present exactly when `source` is `override`. */
  override: {
    /** Who set the Override in the pop-up — not always who released. */
    set_by_name: string | null
    /** The vendor rule the Override wrote, or null when its box was unchecked
        or the invoice had no vendor. */
    rule_id: Uuid | null
    rule_action: RouteOverrideRuleAction | null
    /** The vendor the rule names, by name as it was then; null with no rule. */
    rule_vendor_name: string | null
  } | null
}

/**
 * A routing rule (owner, 2026-09-15; a required-approval set since 2026-10-09 —
 * D-336): "When ..., require the following approvals:", then a flag per step.
 *
 * ALL of a rule's conditions must hold for it to match. When several active
 * rules match, a step is required if ANY of them requires it, and Controller
 * Approval wins over Manager Approval (D-336). There is no name: the rendered
 * sentence is the row's identity, as on every other Rules tab.
 */
export interface OversightRule extends RequiredApprovals {
  id: Uuid
  /** In `position` order. Never empty — a rule with no conditions would be
      vacuously true and would match every invoice in the system. */
  conditions: OversightRuleCondition[]
  /** Where it was created: Rules → Routing (`manual`) or an Override
      (`override`, D-350). Set once and never changed (D-350 amended). */
  source: RoutingRuleSource
  /** Who created it, by name as it was then; null on a rule created before
      2026-10-12 (D-350 amended). */
  created_by_name: string | null
  /** The invoice whose Override created it, with its invoice number as it was
      then; null when `source` is `manual`. Set once, like `source`. */
  override_invoice: { invoice_id: Uuid; invoice_number: string | null } | null
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

/**
 * A routing rule as it matched an invoice, kept on the decision that routed it
 * (D-336): the release from Import Control or a reinstate from Denial Review.
 * A SNAPSHOT — the conditions, their labels and the four flags as they stood
 * at that moment — so History says what the rule said then, whatever the rule
 * says now.
 */
export interface DecisionRoutingRule extends RequiredApprovals {
  rule_id: Uuid
  /** Where the rule was created (D-350). Absent on a snapshot taken before
      2026-10-12, which reads as `manual`. */
  source?: RoutingRuleSource
  /** In `position` order, `value_labels` resolved when the rule matched. */
  conditions: OversightRuleCondition[]
}

/**
 * `POST /invoices/{invoice_id}/routing-preview` (D-347): the invoice as the
 * editor holds it — saved, or with the unsaved edits this body carries, the
 * same `invoice` and `line_items` an intake save would send — checked against
 * the active routing rules. Advisory only: it writes nothing, and the release
 * or reinstate decides the route again from what is saved.
 */
export interface RoutingPreviewRequest {
  invoice?: InvoiceHeaderWrite
  line_items?: {
    upsert?: InvoiceLineItemUpsert[]
    remove?: Uuid[]
  }
}

/**
 * What the rules would require, and each rule that matched, as History shows
 * them. `source` is `rules` when any matched, else `default`: the preview
 * leaves the invoice's pending Override out, so the Override pop-up can say
 * what it is overriding. `vendor_rule` is the vendor's own rule — the one an
 * Override's "Remember this route" box would update — active or not, or null (D-350).
 */
export interface RoutingPreview {
  required: RequiredApprovals
  rules: DecisionRoutingRule[]
  source: RouteSource
  vendor_rule: OversightRule | null
}

export interface RoutingPreviewResponse {
  data: RoutingPreview
}

/** A condition as written. `value_labels` is read-only and never sent. */
export type OversightRuleConditionWrite = Omit<OversightRuleCondition, 'value_labels'>

/**
 * One person emailed when an upload for this company has been read — Rules →
 * Upload Notifications (owner, 2026-10-07 — D-306). The address is the
 * directory's mail address (else the user principal name), stored with the
 * display name when the person was picked; neither is PHI.
 */
export interface UploadNotificationRecipient {
  id: Uuid
  /** The notification this person receives (D-310): `new_upload` or, since D-336,
      `passed_without_approvals`. */
  notification_type: ListedNotificationType
  company_id: string
  /** The company's name, resolved at read time; the id when it no longer resolves. */
  company_name: string
  /** The list the person is on: `ap_ar`, `controller` or `other` for New
      Invoices Upload; null for Invoice Passed Without Any Approvals, which keeps
      one list (`NOTIFICATION_RECIPIENT_KINDS`). */
  kind: UploadNotificationKind | null
  user_email: string
  user_name: string | null
  user_upn: string | null
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

/**
 * One notification sent to the caller, as the Notifications screen lists it (owner,
 * 2026-10-07 — D-310). `GET /notifications`. The bodies are left out of the list.
 */
export interface NotificationSummary {
  id: Uuid
  notification_type: NotificationType
  subject: string
  /** The company the notification was about, resolved at read time; null for none. */
  company_name: string | null
  sent_at: Timestamp
}

/** One notification, whole — the same message the email carried. `GET /notifications/{id}`. */
export interface Notification extends NotificationSummary {
  body_text: string
  /** The email's HTML, every value escaped when it was rendered (`planUploadNotification`). */
  body_html: string
}

/** `POST /upload-notification-recipients`. */
export interface UploadNotificationRecipientCreate {
  /** Which notification the person receives (D-336). Required since code
      review A-11 (2026-10-17); it used to default to `new_upload`. */
  notification_type: ListedNotificationType
  company_id: string
  /** Required for New Upload; omitted or null for Invoice Passed
      Without Any Approvals, which keeps one list. */
  kind?: UploadNotificationKind | null
  user_email: string
  user_name?: string | null
  user_upn?: string | null
}


/**
 * As-printed vendor name → vendor. App-writable since 2026-08-13 (owner item
 * 10b, Rules → Vendors, `source: 'Manual'`). A match against
 * `invoices.vendor_name_as_printed` is a DETERMINISTIC OVERRIDE of vendor
 * identity — it outranks vendor precedent — not merely a ranking preference.
 * See "Rule resolution" in `docs/ai.md` §3.2 and `docs/decisions.md`.
 */
export interface VendorAlias {
  id: Uuid
  /** Compared trimmed and case-insensitive against `invoices.vendor_name_as_printed`. */
  alias_name: string
  vendor_id: string
  vendor_name: string | null
  source: VendorAliasSource
  evidence: string | null
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

/**
 * AS-PRINTED COMPANY NAME -> COMPANY (owner item 18, 2026-08-31,
 * `docs/decisions.md` D-73). Modelled on `VendorAlias` above, and REVERSING
 * the standing "companies never carry aliases" rule that `vendor_aliases`,
 * `docs/schema.md` and `pass2-matching.md` all used to state.
 *
 * Some companies genuinely trade under more than one name — SeniorLIFE
 * Johnstown (`251`) is also billed as `Pennsylvania PACE, Inc.`,
 * `Pennsylvania PACE`, `PA PACE` and `SLJ` — and a printed bill-to name that
 * is one of those identifies the company exactly as well as the master name
 * does.
 *
 * Compared against `invoices.company_name` (the printed bill-to) on the
 * punctuation- and space-blind company key, so `PA PACE`, `PA-PACE` and
 * `papace` are one alias. A hit is a DETERMINISTIC RUNG above pass 2, and it
 * also counts as an exact identification for the ambiguity guard.
 *
 * NO `source` COLUMN, unlike `VendorAlias`: every row is authored — seeded or
 * typed on Rules -> Companies — so a provenance enum would have exactly one
 * value and would cost a Postgres enum to say nothing.
 */
export interface CompanyAlias {
  id: Uuid
  /** Compared trimmed and case-insensitive against `invoices.company_name`. */
  alias_name: string
  company_id: string
  company_name: string | null
  /** Free-text note: where the alias was seen, or who asked for it. */
  evidence: string | null
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  row_version: number
}

/**
 * As-printed vendor -> standing pass-1 extraction guidance, authored from a
 * repeated correction pattern (the training loop, `docs/ai.md` §9). Modelled
 * on `VendorAlias` above: org-wide, no RLS, ONE ACTIVE row per vendor.
 *
 * `field` and `symptom` are closed vocabularies owned by
 * `backend/src/domain/vendorGuidance.ts` (`QUALIFYING_GUIDANCE_FIELDS`,
 * `GUIDANCE_SYMPTOMS`) — carried here as plain `string` rather than a literal
 * union, the same choice this package makes for other backend-validated free
 * vocabularies, so a new value does not require a wire-type edit on this side.
 * They are the AUTHORING LOOP'S BOOKKEEPING and, since the owner ruling of
 * 2026-08-28, are neither shown on the Rules -> Guidance tab nor writable
 * through it: guidance is vendor-scoped free text.
 *
 * `correction_rate_*` are computed at READ time (qualifying corrections per
 * invoice for this vendor, before vs after this row took effect) — not
 * stored columns, so they always reflect current data.
 */
export interface VendorExtractionGuidance {
  id: Uuid
  /**
   * THE KEY since 2026-09-11 (M1). Guidance is tied to the real vendor, and the
   * host resolves whatever the letterhead printed to this id before pass 1 is
   * built. Never null on the wire: the legacy rows the 2026-09-11 migration
   * could not resolve to a vendor are retired and are not returned at all.
   */
  vendor_id: string
  /** Resolved at read time by a join onto `vendors`. */
  vendor_name: string | null
  /**
   * WAS the key, now a record of what was printed when this row was authored.
   * Null on a hand-authored row, which has no source invoice.
   */
  extracted_vendor_value: string | null
  /** The one other content-derived string the PHI filter exempts. */
  company_name_as_printed: string | null
  field: string
  symptom: string
  /** The authoring model's (or a human editor's) own free text. Bounded by
      `GUIDANCE_INSTRUCTION_MAX_CHARS` (5,000, `backend/src/domain/vendorGuidance.ts`),
      which is an abuse guard rather than a content rule since 2026-09-14 — the 400
      that stood here was the budget for injecting every active row into every pass-1
      prompt, and guidance is looked up per vendor now. The authoring model's own
      output is still held to 400 by the `emit_guidance` tool schema. */
  instruction: string
  /** The host-rendered sentence actually injected into pass 1 (`renderGuidanceSentence`). */
  guidance_text: string
  guidance_version: number
  superseded_by: Uuid | null
  is_active: boolean
  retired_at: Timestamp | null
  consecutive_non_improving_supersedes: number
  /** Three supersedes without the correction rate falling — flagged for a human, not superseded a fourth time. */
  convergence_stopped: boolean
  applied_count: number
  last_applied_at: Timestamp | null
  source_invoice_id: Uuid | null
  created_at: Timestamp
  row_version: number
  /** Qualifying corrections per invoice for this vendor BEFORE this row took effect. `null` with no eligible history. */
  correction_rate_before: number | null
  /** Qualifying corrections per invoice for this vendor AFTER this row took effect. `null` with no eligible history yet. */
  correction_rate_after: number | null
}

export interface Vehicle {
  id: Uuid
  /** The fleet owner's vehicle number. Unique among active rows — it is the tag. */
  number: string
  vin: string | null
  license_plate: string | null
  make: string | null
  model: string | null
  /** Text, not a number — arrives from their export and may carry `2009/2010`. */
  year: string | null
  /** The fleet owner's own text. NOT a company FK and NOT a partition key. */
  company_label: string | null
  department: string | null
  /** The fleet director's labels, verbatim. The ONLY input to `is_active`. */
  status: VehicleStatus
  /**
   * DERIVED, never sent by a client: `vehicleIsActiveFor(status)` — `In Service`
   * is active, `Out of Service` and `Sold` are not (D-65). Every write path
   * computes it, and the database CHECKs it.
   */
  is_active: boolean
  retired_at: Timestamp | null
  created_at: Timestamp
  updated_at: Timestamp
  row_version: number
}

// Fleet spreadsheet import.

/** The columns the fleet listing maps onto, in the owner's own column order. */
export type FleetField =
  | 'number'
  | 'vin'
  | 'license_plate'
  | 'make'
  | 'model'
  | 'year'
  | 'company_label'
  | 'department'
  | 'status'

interface FleetHeaderMappingDto {
  by_field: Partial<Record<FleetField, number>>
  /** Headers we could not place — surfaced so nothing is silently dropped. */
  unmapped: string[]
  missing: FleetField[]
  /** True when every field matched a synonym with no model involved. */
  deterministic: boolean
}

interface FleetFieldChangeDto {
  field: FleetField
  before: string
  after: string
}

export interface VehicleImportPreviewResponse {
  preview_token: string
  expires_at: Timestamp
  sheet_name: string
  /** The row the header was found on, 1-based — the row number the
      spreadsheet shows, like every `row` below (D-289). */
  header_row: number
  mapping: FleetHeaderMappingDto
  adds: Array<{ row: number; values: Partial<Record<FleetField, string | null>> }>
  updates: Array<{ row: number; vehicle_id: Uuid; number: string; changes: FleetFieldChangeDto[] }>
  unchanged_count: number
  /** Reported and NEVER changed: a partial export must not retire half the fleet. */
  absent: Array<{ vehicle_id: Uuid; number: string }>
  skipped: Array<{ row: number; reason: string }>
  duplicates: Array<{ number: string; rows: number[] }>
}

export interface VehicleImportCommitResponse {
  added: number
  updated: number
  unchanged: number
  absent_untouched: number
}

// Point Click Care vendor-list import (owner item 12, 2026-08-19).
//
// Deliberately its own DTOs rather than reused fleet-import types: the match
// key is `vendor_id` (never `vendor_name` — 95 real duplicate-name groups
// exist), and a blank cell means "leave the stored value alone" rather than
// the fleet rule of "a blank cell is a real value, clear it" — see
// `backend/src/domain/vendorImport.ts`'s header comment for why the file
// forces that reversal.

/** The columns we write, in the owner's mapping-table order. `vendor_id` is
 *  the match key, not itself an updatable field on a matched row. */
export type VendorField =
  | 'vendor_id'
  | 'vendor_name'
  | 'address_1'
  | 'address_2'
  | 'address_3'
  | 'city'
  | 'state_or_province'
  | 'postal_code'
  | 'phone'
  | 'fax'
  | 'remittance_address_1'
  | 'remittance_address_2'
  | 'remittance_address_3'
  | 'remittance_city'
  | 'remittance_state_or_province'
  | 'remittance_postal_code'

interface VendorHeaderMappingDto {
  by_field: Partial<Record<VendorField, number>>
  /** Column index of the file's `Active` column, when it has one — read for
   *  the vendor's status, never written as a field (D-390). */
  active?: number
  /** Every column no field claimed — including the rest of the "Remit To
   *  Different Address" block (its address fields are the Remittance Address,
   *  D-241) and `Default G/L Accounts` (deliberately not imported:
   *  it overlaps `coding_rules`, which is routing owned by the Rules
   *  screen, not reference data). */
  unmapped: string[]
  missing: VendorField[]
  deterministic: boolean
}

interface VendorFieldChangeDto {
  field: Exclude<VendorField, 'vendor_id'>
  before: string
  after: string
}

export interface VendorImportPreviewResponse {
  preview_token: string
  expires_at: Timestamp
  mapping: VendorHeaderMappingDto
  /** `inactive`: the file lists the new vendor as inactive, so it is added
   *  inactive (D-390). */
  adds: Array<{
    row: number
    values: Partial<Record<Exclude<VendorField, 'vendor_id'>, string | null>> & { vendor_id: string }
    inactive: boolean
  }>
  // `id` is the vendor ROW's own uuid (`Vendor.id`) — distinct from
  // `vendor_id`, which is the PCC business code and already means something
  // else everywhere in this codebase (`CodingRule.vendor_id`,
  // `VendorAlias.vendor_id`, …). Naming it `vendor_id` here, the way the
  // fleet import names its row key `vehicle_id`, would collide with that.
  updates: Array<{ row: number; id: Uuid; vendor_id: string; vendor_name: string; changes: VendorFieldChangeDto[] }>
  unchanged_count: number
  /**
   * ACTIVE vendors the file does not mention — **marked inactive on commit**
   * (owner ruling, 2026-08-28: the Point Click Care list is the whole vendor
   * list, so a vendor that has left it has left the system). The retire is
   * soft: `is_active` goes false and `retired_at` is stamped, nothing is
   * deleted, and `vendor_id` — a foreign key from `coding_rules`,
   * `vendor_aliases`, `invoices` and `vendor_extraction_guidance` — still
   * resolves. It can be a third of the master in one click (1,993 of the real
   * 6,210), so `absent_total` is the count the preview must show before the
   * user commits. Truncated to `VENDOR_IMPORT_ABSENT_PREVIEW_LIMIT` entries —
   * `absent_total` carries the true count so the UI can say "showing 500 of
   * 1,993" rather than shipping the whole list on the wire.
   */
  absent: Array<{ vendor_id: string; vendor_name: string }>
  absent_total: number
  /** ACTIVE vendors the file lists as inactive in its `Active` column — made
   *  inactive on commit, beside `absent` (D-390). */
  marked_inactive: Array<{ vendor_id: string; vendor_name: string }>
  /** INACTIVE vendors the file lists as active — made active on commit (D-390). */
  marked_active: Array<{ vendor_id: string; vendor_name: string }>
  skipped: Array<{ row: number; reason: string }>
  duplicates: Array<{ vendor_id: string; rows: number[] }>
}

export interface VendorImportCommitResponse {
  added: number
  updated: number
  unchanged: number
  /** How many active vendors this commit marked inactive: those the file did
   *  not mention, and those it marked inactive (D-390). The fleet import's twin
   *  field is `absent_untouched`, and the difference in name is the difference
   *  in behaviour. */
  deactivated: number
  /** How many inactive vendors the file listed as active, made active (D-390). */
  activated: number
}

// ---------------------------------------------------------------------------
// 16. Analytics — aggregates only, all row-level-security scoped
// ---------------------------------------------------------------------------

export interface AnalyticsEnvelope {
  as_of: Timestamp
  filters_applied: Record<string, unknown>
}

export interface AnalyticsSpendByEntityResponse extends AnalyticsEnvelope {
  entities: Array<{
    entity_type: EntityType
    entity_key: string
    entity_name: string | null
    count: number
    total: MoneyString
  }>
  /** True when more than the 300 highest-spend entities matched. */
  truncated: boolean
}

/**
 * `GET /analytics/corrections` (§16.6). Every correction count is of DISTINCT
 * (invoice, line or header, field) a person changed (D-368): a derived
 * Department and a Combine's bookkeeping rows are left out, and `by_field`
 * carries a combined line as `LineCombined` rather than `LineRemoved`.
 */
export interface AnalyticsCorrectionsResponse extends AnalyticsEnvelope {
  by_field: Array<{ field: string; count: number }>
  by_vendor: Array<{ vendor_id: string | null; vendor_name: string | null; count: number }>
  /** The Dashboard's four flag panels (owner, 2026-10-16 — D-368). */
  flags: AnalyticsFlags
}

/** The flags half of §16.6, over the same filtered invoices as the corrections. */
interface AnalyticsFlags {
  /** Active flags, resolved or not, per the invoice's vendor; most first. */
  by_vendor: Array<{ vendor_id: string | null; vendor_name: string | null; count: number }>
  /** Active flags, resolved or not, per stored flag type; most first. */
  by_type: Array<{ flag_type: string; count: number }>
  /**
   * Per week the invoices were uploaded (weeks start Monday midnight Eastern;
   * `YYYY-MM-DD`), the median count of active flags per invoice, a flagless
   * invoice as 0, and the invoices it is taken over. Oldest week first; an
   * invoice with no `uploaded_at` is in no week.
   */
  median_by_week: Array<{ week_start: string; median: number; invoice_count: number }>
  /**
   * REQUIRED CORRECTIONS PER FLAG. Per flag type, of its RESOLVED flags: how many saw a person's correction on
   * the same invoice (the same line, for a line-scoped flag) between being
   * raised and being resolved. Sorted by `corrected_count / resolved_count`,
   * highest first.
   */
  needed_correction_by_type: Array<{ flag_type: string; resolved_count: number; corrected_count: number }>
}

/**
 * One tile of the dashboard's standing KPI block (§16.9).
 *
 * `count` and `total` describe the SAME set of invoices, always — a tile that
 * counted one population and priced another is the defect the four-measure
 * breakdown below is also shaped to avoid.
 */
export interface AnalyticsKpiTile {
  count: number
  total: MoneyString
}

/**
 * The four standing figures at the top of the dashboard (§16.9).
 *
 * DELIBERATELY UNFILTERED. This endpoint takes no query parameters at all: the
 * block answers "how is the queue doing right now", and a reader who has
 * narrowed the panels below to one vendor still needs the unnarrowed answer
 * above them. Row-level security still applies — it always does — so a company
 * user sees their own slice and an internal user sees the organization.
 *
 * `week_start` is the Monday the two weekly tiles are measured from, at
 * midnight Eastern (H-08), echoed so a screenshot says which week it means.
 * `past_due_days` is the threshold the past-due tile applied, for the same
 * reason.
 */
export interface AnalyticsKpisResponse extends AnalyticsEnvelope {
  week_start: DateOnly
  past_due_days: number
  /** Arrived this week — `uploaded_at` on or after `week_start`. */
  imported_this_week: AnalyticsKpiTile
  /** Everything not yet terminal, all time. */
  in_progress: AnalyticsKpiTile
  /** Not yet terminal AND uploaded more than `past_due_days` days ago. */
  past_due: AnalyticsKpiTile
  /** Reached Point Click Care this week, by either route. */
  exported_this_week: AnalyticsKpiTile
  /**
   * The `in_progress` invoices split by AGE, for the block's progress bar.
   *
   * AGED FROM `uploaded_at`, not from the last status write — the same clock
   * `past_due` uses.
   *
   * THE BANDS SUM TO `in_progress.count`, exactly, and that is the arithmetic
   * to rely on. The two do NOT partition each other at the boundary, and the
   * reason is worth knowing rather than papering over: the tile compares
   * TIMESTAMPS (`uploaded_at < now - 7 days`) while a band compares WHOLE DAYS
   * (`floor(age) <= 7`), so an invoice seven and a half days old is past due
   * AND sits in the 0-7 band. Forcing agreement would mean bending one of them
   * away from what it says — the tile away from "uploaded over seven days ago",
   * or the bands away from their inclusive whole-day bounds.
   *
   * One entry per `OUTSTANDING_AGE_BANDS` band, in order, present even at zero — a bar with
   * a band missing would rescale silently.
   */
  outstanding_by_age: Array<{
    label: string
    min_days: number
    max_days: number | null
    count: number
    total: MoneyString
  }>
  /**
   * In progress, but with no `uploaded_at` to age from.
   *
   * Practically always zero: every intake path stamps it. It is on the wire
   * anyway because the alternative is worse — such an invoice would count in
   * `in_progress` and appear in no band, so the bar would quietly fail to add
   * up to the tile above it, and nothing on screen would say why.
   */
  outstanding_unknown_age: AnalyticsKpiTile
}

/**
 * One row of a dimensional breakdown (§16.7) — four measures over one key.
 *
 * THE FOUR MEASURES DO NOT SHARE A POPULATION, and the difference is the point
 * rather than a defect to paper over:
 *
 *  * `invoice_count` / `total` are the invoices that match the filters and sit
 *    at this key NOW. Spend is their printed total.
 *  * `median_days` is a DURATION over completed work — for `step`, the days an
 *    invoice spent at that step before leaving it; for `vendor` and `company`,
 *    the days from arrival to a terminal status. Null when nothing has finished
 *    yet, never `0`, which would assert a fact nobody has observed.
 *  * `denial_count` counts denial-shaped decisions (shared `DENIAL_OUTCOMES`),
 *    and `hold_count` counts holds ever placed. Both are lifetime counts on the
 *    filtered invoices, so a row can carry denials with no invoice standing at
 *    it today.
 */
export interface AnalyticsDimensionRow {
  /** The node key, vendor id, or company id. `(none)` where the row has none. */
  key: string
  /** What to print. Never blank: it falls back to the key rather than to ''. */
  label: string
  invoice_count: number
  total: MoneyString
  median_days: number | null
  denial_count: number
  hold_count: number
  /**
   * The MEDIAN SECONDS A PERSON SPENT on one invoice in this row, and how many
   * times invoices here were opened (§16.7a).
   *
   * A DIFFERENT MEASURE FROM `median_days`, and the difference is the point.
   * `median_days` is elapsed time — how long the invoice sat somewhere,
   * lunch and weekends included. This is handling time: seconds with the tab
   * visible and someone actually working, summed per invoice and then taken
   * across invoices. One says whether the process is slow; the other says
   * whether the tool is.
   *
   * `null` until sessions exist for the row, which is every row before this
   * shipped — there is no backfill and there cannot be one.
   */
  median_handling_seconds: number | null
  touch_count: number
}

export interface AnalyticsByDimensionResponse extends AnalyticsEnvelope {
  dimension: AnalyticsDimension
  /** Ordered by `invoice_count` descending: the top 50 rows by EACH measure,
      together (H-01). See `truncated`. */
  rows: AnalyticsDimensionRow[]
  /** True when the cap left rows out, so a client can say so. Each measure's
      ranking is still complete to 50. */
  truncated: boolean
}

/**
 * Line-item spend rolled up by one of the three coding fields (§16.8).
 *
 * The grain is the LINE ITEM, not the invoice: an invoice coded across four
 * codes contributes its four line totals to four rows, so the column sums to
 * coded spend rather than to four copies of one invoice total. `count` is the
 * number of invoices contributing, which is why the counts across rows sum to
 * more than the number of invoices.
 */
export interface AnalyticsSpendByCodeResponse extends AnalyticsEnvelope {
  group_by: SpendGrouping
  rows: Array<{ key: string; label: string; count: number; total: MoneyString }>
  truncated: boolean
}

// ---------------------------------------------------------------------------
// 17. Corrections — capture-only, no write routes
// ---------------------------------------------------------------------------

export interface Correction {
  id: Uuid
  parent_id: Uuid
  record_type: WorkspaceKind
  /** The line item, NULL for a header-level field. */
  child_id: Uuid | null
  /** A Postgres column name; the pseudo-events `LineAdded` / `LineRemoved` and
      `EntityAdded` / `EntityRemoved`; or a tag's field, `entity_name`,
      `entity_vehicle_id`, `entity_staff_upn` or `entity_line_item_id` (D-328,
      `TAG_CORRECTION_FIELD` in `backend/src/domain/taggedEntities.ts`). */
  field: string
  old_value: string | null
  new_value: string | null
  old_source: string | null
  /** The step the edit was made at. NULL from the matching worker. May be the
      retired `AP/AR Approval Decision` on a row written before 2026-09-29 (D-215). */
  step: RecordedDecisionStep | null
  vendor_id: string | null
  vendor_name: string | null
  corrected_by_name: string | null
  corrected_by_email: string | null
  corrected_at: Timestamp
}

// ---------------------------------------------------------------------------
// 18. Ops
// ---------------------------------------------------------------------------

export interface HealthResponse {
  status: 'ok' | 'degraded'
  version: string
  db: 'ok' | 'error'
  checked_at: Timestamp
  details?: { failed: string[] }
}

// ---- Reports (owner, 2026-09-10) -------------------------------------------
//
// `ReportCriteria` and `ReportInvoiceRow` live in `report.ts`, not here, because
// `describeCriteria` needs them and behaviour does not belong in a types file.

/** `POST /reports`. The criteria echoed back are the NORMALIZED ones the server
    stored on the audit row and ran the query with — not necessarily byte-identical
    to what was sent, which is the point of echoing them. */
export interface CreateReportResponse {
  report_id: Uuid
  requested_at: string
  criteria: ReportCriteria
  sort: ReportSort
  invoice_count: number
  invoices: ReportInvoiceRow[]
  /**
   * Invoices that match the criteria and belong to one of the runner's Reporting
   * companies but are LEFT OUT, because they sit at Import Control with an
   * unresolved company flag (owner, 2026-10-13 — D-354). The cover says so in a
   * warning block under Criteria. A count only: never which invoices.
   */
  omitted_company_flag_count: number
}

/** One row of `GET /reports` — what was asked for, by whom, when. Carries no
    invoice values, only the count and the ids. */
export interface ReportRequestSummary {
  report_id: Uuid
  /** The requester's Entra object id. */
  requested_by: string
  /** Their display name as it was when the report ran, or `null` when the
      principal carried none. Denormalized at write time, like
      `user_roles.user_name` — the listing shows a person, not a GUID. */
  requested_by_name: string | null
  requested_at: string
  criteria: ReportCriteria
  sort: ReportSort
  invoice_count: number
}

/**
 * `GET /reports/{report_id}` — the SAME INVOICES a recorded report covered,
 * fetched again (owner, 2026-09-14). It re-reads them by the ids the audit row
 * stored, never by re-running the criteria, because a criterion like Status
 * moves and the point is to see what was seen.
 *
 * `invoices` is therefore the stored SET, in the stored ORDER, carrying today's
 * values. The set is pinned; the values are not, and cannot be — nothing here
 * stores an invoice's past.
 *
 * `unavailable_count` is `invoice_count` minus the rows that came back: the
 * recorded invoices the caller cannot see now (D-258). The re-read runs under
 * their current Reporting seats, so an invoice drops out when the seat for its
 * company was made Inactive, when its company changed to one they hold no seat
 * for, or when the lens now leaves it out (at Import Control with no company or
 * an unresolved company flag). Nothing grants a delete. The server logs
 * `report_download_incomplete` whenever it is non-zero.
 */
export interface ReportContentsResponse {
  report_id: Uuid
  /** The requester's Entra object id. */
  requested_by: string
  /** Their display name as it was when the report ran, or `null` when the
      principal carried none. Denormalized at write time, like
      `user_roles.user_name` — the listing shows a person, not a GUID. */
  requested_by_name: string | null
  requested_at: string
  criteria: ReportCriteria
  sort: ReportSort
  /** What the report covered WHEN IT RAN — the recorded count, not `invoices.length`. */
  invoice_count: number
  invoices: ReportInvoiceRow[]
  unavailable_count: number
}

// --- Ops: application errors and deploy reports (Admin → Errors, 2026-09-14) ----
// `docs/api.md` §18.2 / §18.3. Every field here is one the structured logger
// already allow-lists (`backend/src/platform/log.ts`); the row builder in
// `backend/src/domain/opsErrors.ts` copies ONLY these names, so a new field
// reaches the page by being named here, there and in the contract — never by
// the allow-list growing for the logger's own reasons.

export type OpsErrorKind = 'worker' | 'api' | 'bedrock'

export interface OpsErrorRow {
  at: string
  function: string
  event: string
  level: 'error' | 'warn'
  kind: OpsErrorKind
  error_class?: string
  reason?: string
  upload_id?: string
  invoice_id?: string
  message_id?: string
  export_id?: string
  request_id?: string
  route?: string
  method?: string
  http_status?: number
  model_id?: string
  attempt?: number
}

export type OpsErrorsWindow = '24h' | '7d' | '30d'

export type OpsErrorsResponse =
  | { status: 'complete'; window: OpsErrorsWindow; rows: OpsErrorRow[]; truncated: boolean; scanned_bytes: number }
  | { status: 'running'; query_id: string }

/** One build's deploy report, as the pipeline wrote it to `deploys/<build-id>.json`. */
export interface DeployReport {
  build_id: string
  at: string
  status: 'SUCCEEDED' | 'FAILED'
  sha: string
  subject: string
  failed_step: 'verify' | 'build' | 'deploy' | 'bootstrap' | null
  body: string
}

export interface DeployReportsResponse {
  data: DeployReport[]
}

// ---------------------------------------------------------------------------
// 21. View sessions — handling time (owner, 2026-09-20)
// ---------------------------------------------------------------------------

/**
 * One timed viewing session, as the browser measured it.
 *
 * The clock is the CLIENT's, because only the client knows whether the tab was
 * visible and whether anybody was typing. The server does not trust it blindly:
 * `normalizeViewSession` clamps every field before the insert, and the row
 * carries `was_capped` when a clamp bit.
 */
export interface ViewSessionInput {
  /**
   * Minted by the client, one per session, and the natural key the insert is
   * idempotent on. The close flush and the tab-hidden flush can both fire for
   * one session; sending the same id twice writes one row.
   */
  client_session_id: string
  invoice_id: string
  /** The invoice's status when the session OPENED, not now. */
  status: InvoiceStatus
  kind: ViewSessionKind
  opened_at: string
  ended_at: string
  /** Seconds the tab was visible AND the user was active. Zero for a bulk action. */
  active_seconds: number
  /** The rest of the span. Kept so the idle threshold can be re-cut from history. */
  idle_seconds: number
}

export interface CreateViewSessionsResponse {
  /** Rows written. Lower than the number sent when a `client_session_id` repeats. */
  written: number
  /**
   * Sessions dropped before the insert, by reason — an unparseable timestamp, a
   * span the wrong way round, an unknown invoice. Counts only: a rejected
   * measurement is not worth an error to a user who cannot act on it, and the
   * endpoint never fails a page for one.
   */
  rejected: number
}
